import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Field } from 'react-final-form'
import { OnChange } from 'react-final-form-listeners'
import { enumToTitleCase } from '../../utilities/stringUtilities'
import { useSelector } from 'react-redux'
import UnderlineSelect from '../underline-select'
import classNames from 'classnames'

export default function DefaultBillingStatusInputs ({
  billingStatuses,
  billingStatusManuallyUpdated,
  resourceTypeBillingStatusSelected,
  resourceTypes,
  setResourceTypeBillingStatusSelected,
  setTicketTypeBillingStatusSelected,
  ticketTypeBillingStatusSelected,
  ticketTypes,
  values,
  serviceFrequency,
  form,
  initialAssetValues
}) {
  const { hauler } = useSelector(({ user: { hauler } }) => ({ hauler }))
  const isRecurring = serviceFrequency === 'RECURRING' && hauler.isRecurringServiceEnabled

  const defaultSelectedResourceTypes = useMemo(() => {
    const assetDetails = form?.getState().values?.assetDetails || []
    if (assetDetails.length === 0) return []

    return assetDetails
      .map(a => resourceTypes.find(opt => opt.id === a.resourceTypeId))
      .filter(Boolean)
  }, [form, resourceTypes])

  function billingStatusMessaging (manuallyUpdated, value) {
    if (manuallyUpdated || !value) {
      return ''
    } else if (value === resourceTypeBillingStatusSelected) {
      return 'Using asset type default'
    } else if (value === ticketTypeBillingStatusSelected) {
      return 'Using ticket type default'
    } else {
      return 'Using general default'
    }
  }

  function onTicketTypeChange (val) {
    const selected = ticketTypes.find(type => type.id === val)
    setTicketTypeBillingStatusSelected(selected?.defaultBillingStatus)
    if (val) {
      return resourceTypeBillingStatusSelected || selected.defaultBillingStatus || 'PENDING'
    }
    return null
  }

  function onResourceTypeChange (val) {
    const selected = resourceTypes.find(type => type.id === val)
    setResourceTypeBillingStatusSelected(selected?.defaultBillingStatus)
    if (val) {
      return selected?.defaultBillingStatus || ticketTypeBillingStatusSelected || 'PENDING'
    }
    return null
  }

  return (
    <>
      <Field name='billingStatus' subscription={{}}>
        {(
          { input: { onChange } }
        ) => (
          <OnChange name='ticketTypeId'>
            {() => {
              if (!billingStatusManuallyUpdated) {
                onChange(onTicketTypeChange(values.ticketTypeId))
              }
            }}
          </OnChange>
        )}
      </Field>

      <Field name='billingStatus' subscription={{}}>
        {(
          { input: { onChange } }
        ) => (
          <OnChange name='resourceTypeId'>
            {() => {
              if (!billingStatusManuallyUpdated) {
                onChange(onResourceTypeChange(values.resourceTypeId))
              }
            }}
          </OnChange>
        )}
      </Field>

      <div className={classNames({ 'dis-form-container': isRecurring })}>
        <div className={classNames({ 'dis-form-row': isRecurring })}>
          <Field name='ticketTypeId'>
            {({ input }) => (
              <div className={classNames('form-group', { 'dis-third-width-field': isRecurring })}>
                <label htmlFor='ticketTypeId' className='control-label'>Ticket Type</label>
                <select
                  {...input}
                  type='select'
                  className='form-control'>
                  <option key='placeholder' value=''></option>
                  {ticketTypes.map((tt) => (
                    <option key={tt.id} value={tt.id}>
                      {`${tt.name} (${tt.shortCode})`}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </Field>

          <div className={classNames('form-group', { 'dis-third-width-field': isRecurring })}>
            <label className='control-label' htmlFor={`${isRecurring ? 'assetTypeSelect' : 'resourceTypeId'}`}>Asset Type</label>
            {isRecurring
              ? (
                <Field name='assetTypeSelect'>
                  {({ input }) => (
                    <UnderlineSelect
                      placeholder={''}
                      isMulti
                      inputId='assetTypeSelect'
                      options={resourceTypes}
                      defaultValue={defaultSelectedResourceTypes}
                      onChange={(selectedOptions) => {
                        const selectedIds = selectedOptions?.map(o => o.value) || []
                        const existing = form?.getState()?.values?.assetDetails?.filter(a =>
                          a && selectedIds.includes(a.resourceTypeId)
                        ) || []
                        const added = selectedIds
                          .filter(id => !existing?.some(a => a.resourceTypeId === id))
                          .map(id => ({
                            resourceTypeId: id,
                            ...initialAssetValues
                          }))
                        form.change('assetDetails', [...existing, ...added])
                      }}
                      minWidth={230}
                      maxWidth={250}
                      closeMenuOnSelect
                    />
                  )}
                </Field>
                )
              : (
                <Field name='resourceTypeId'>
                  {({ input }) => (
                    <select {...input} className='form-control'>
                      <option value='' />
                      {resourceTypes.map(rt => (
                        <option key={rt.id} value={rt.id}>
                          {`${rt.name} (${rt.shortCode})`}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
                )}
          </div>

          <div className={classNames({ 'dis-third-width-field': isRecurring })}>
            <Field name='billingStatus'>
              {({ input }) => (
                <div className='form-group'>
                  <label htmlFor='billingStatus' className='control-label'>Billing Status</label>
                  <select
                    {...input}
                    type='select'
                    className='form-control'>
                    <option key='placeholder' value=''></option>
                    {Object.keys(billingStatuses).map((opt) => (
                      <option value={opt} key={opt}>
                        {enumToTitleCase(opt)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </Field>
            <h6><em>{billingStatusMessaging(billingStatusManuallyUpdated, values.billingStatus)}</em></h6>
          </div>
        </div>
      </div>
    </>
  )
}

DefaultBillingStatusInputs.propTypes = {
  billingStatuses: PropTypes.object.isRequired,
  billingStatusManuallyUpdated: PropTypes.bool,
  resourceTypes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    shortCode: PropTypes.string.isRequired
  })).isRequired,
  resourceTypeBillingStatusSelected: PropTypes.string,
  setResourceTypeBillingStatusSelected: PropTypes.func,
  setTicketTypeBillingStatusSelected: PropTypes.func,
  ticketTypeBillingStatusSelected: PropTypes.string,
  ticketTypes: PropTypes.array.isRequired,
  values: PropTypes.object,
  serviceFrequency: PropTypes.string,
  form: PropTypes.object,
  initialAssetValues: PropTypes.object
}
